import type { Dict } from '../types'

/**
 * Swedish. Vocabulary and tone follow MOS MOSH's own Swedish trade-in copy (Core
 * `brandLocalization.tradein2`): informal "du", inbyte/byta in for the trade-in, varor/plagg for the
 * items, fraktetikett, inlämningsställe. Translated best-effort — please proofread.
 */
export const sv: Dict = {
  languageName: 'Svenska',

  common: {
    points: 'poäng',
  },

  // Subject-word lexicon (see en.ts). Swedish defaults for clothing (kläder = plural), so the copy
  // agrees in the plural ("Kläder som inte uppfyller våra krav doneras…"). A brand supplies the full
  // phrase per form, so a singular common-gender word (t.ex. "smycket") brings its own agreement.
  //
  // värdebevis is neuter (ett värdebevis), mirroring Danish værdibevis — the few strings that carry
  // their own determiner ("ditt", "ett", "klart") are flagged where they appear. MOS MOSH overrides
  // this to "voucher" via tradeInConfig.terms.sv; nothing here says the word literally.
  subject: {
    noun: 'kläder',
    nounPoss: 'dina kläder',
    nounDef: 'kläderna',
    reward: 'värdebevis',
    rewardPoss: 'ditt värdebevis',
    rewardDef: 'värdebeviset',
    rewardPlural: 'värdebevis',
  },

  auth: {
    signingIn: 'Loggar in dig…',
    stillWorking: 'Arbetar fortfarande — vi hämtar dina tidigare beställningar. Det kan ta ett ögonblick.',
    loadingItems: 'Hämtar dina varor…',
    errorTitle: 'Vi kunde inte starta ditt inbyte',
    errorGeneric: 'Något gick fel när du skulle loggas in.',
    missingShop: 'Appen är felkonfigurerad — shopId saknas.',
    needSignIn: 'Du måste logga in innan du kan byta in.',
    signInFailed: 'Inloggningen misslyckades. Försök igen.',
    linkInvalid: 'Den här inloggningslänken fungerar inte längre. Profilen den hör till kan ha raderats. Gå tillbaka till startsidan för att logga in igen.',
    backToFrontPage: 'Tillbaka till startsidan',
  },

  config: {
    title: 'Konfigurationsfel',
    missingShop: 'Detta inbyte saknar sin butikskonfiguration (shopId). Kontakta butiken.',
  },

  login: {
    title: 'Ge {nounPoss} nytt liv',
    subtitle: 'Logga in för att starta ditt inbyte och förvandla plagg du inte längre bär till ett {reward}.',
    emailLabel: 'E-post',
    emailPlaceholder: 'du@exempel.se',
    start: 'Fortsätt',
    working: 'Vänta…',
    emailSentTitle: 'Kolla din e-post',
    emailSentBody: 'Vi har skickat en inloggningslänk till {email}. Öppna den på den här enheten för att fortsätta.',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '070 123 45 67',
    sendCode: 'Skicka kod',
    codeLabel: 'Verifieringskod',
    codePlaceholder: '6-siffrig kod',
    verify: 'Verifiera & fortsätt',
    changePhone: 'Använd ett annat nummer',
    back: 'Tillbaka',
    methodSms: 'Logga in med SMS',
    methodSmsDesc: 'Vi skickar dig en kod via sms — om vi har ditt telefonnummer registrerat.',
    methodEmail: 'Logga in med e-post',
    methodEmailDesc: 'Vi skickar en inloggningslänk till din inkorg.',
    or: 'eller',
    wholesale: 'Köpt i butik eller fått i present?',
    emailInvalid: 'Ange en giltig e-postadress.',
    phoneInvalid: 'Ange ett giltigt telefonnummer.',
    codeInvalid: 'Koden fungerade inte. Försök igen.',
    failed: 'Något gick fel. Försök igen.',
    step1Title: 'Hitta dina plagg',
    step1Body: 'Sök eller ta ett foto på det du inte längre bär.',
    step2Title: 'Skicka in dem',
    step2Body: 'Packa allt i ett paket med din förbetalda fraktetikett.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'Få {rewardPoss} när vi har tagit emot dina varor.',
      handin: 'Få {rewardPoss} så snart ditt paket har skannats på inlämningsstället.',
      instant: 'Få {rewardPoss} direkt — skicka sedan dina varor till oss.',
    },
  },

  country: {
    title: 'Ditt land stöds kanske inte',
    body: 'Det verkar som att vi kanske inte har någon fraktlösning som kan skicka ett inbyte från ditt land till vårt lager. Du är välkommen att fortsätta, men vi kan kanske inte slutföra insamlingen.',
    dismiss: 'Fortsätt ändå',
  },

  menu: {
    open: 'Meny',
    language: 'Språk',
    myTradeIns: 'Mina inbyten',
    howItWorks: 'Så fungerar det',
    noneYet: 'Inga än',
    logout: 'Logga ut',
    soon: 'Snart',
  },

  // Värdebevisrader, återanvänds av sidan "Mina inbyten" för värdebevissektionen.
  rewards: {
    activeHeading: 'Tillgängliga',
    spentHeading: 'Använda',
    expires: 'Går ut {date}',
    active: 'Aktivt',
    spentBadge: 'Använt',
  },
  // Kontosidan "Mina inbyten": värdebevis först, sedan inbyteshistorik + detaljdialog.
  myTradeIns: {
    title: 'Mina inbyten',
    subtitle: 'Dina inbyten och {rewardPlural}.',
    back: 'Tillbaka till inbytet',
    loading: 'Hämtar dina inbyten…',
    empty: 'Du har inga inbyten eller {rewardPlural} än.',
    historyHeading: 'Dina inbyten',
    tradeInPrefix: 'Inbyte',
    itemsCount: '{count} varor',
    statusReceived: 'Mottaget',
    statusOnItsWay: 'På väg till oss',
    statusAwaiting: 'Väntar på ditt paket',
    detailTitle: 'Inbytesdetaljer',
    nextHeading: 'Vad händer nu',
    nextReceived: 'Vi har tagit emot dina varor — {rewardPoss} är på väg.',
    nextTransit: 'Ditt paket är på väg till oss. Vi mejlar dig så snart vi har tagit emot och kontrollerat dina varor.',
    nextPickup: 'Ta med dina varor till vår butik, så sköter vi resten.',
    nextShip: 'Packa dina varor och skicka dem till oss med uppgifterna nedan.',
    itemsHeading: 'Varor i detta inbyte',
    labelUnavailable: 'Fraktetiketten är inte längre tillgänglig.',
    close: 'Stäng',
  },

  steps: {
    items: 'Varor',
    story: 'Din berättelse',
    details: 'Uppgifter',
    shipping: 'Frakt',
    confirm: 'Bekräfta',
  },

  rail: {
    title: 'Ditt inbyte',
    itemCount: '{count} vara',
    itemCountPlural: '{count} varor',
    empty: 'Inga varor än — lägg till din första och se värdet växa.',
    loading: 'Hämtar ditt inbyte…',
    estimatedReward: 'Totalt värde',
    rewardNoteConfirmed: {
      received: 'Bekräftas när vi tar emot dina varor',
      handin: 'Bekräftas när du lämnar in ditt paket',
      instant: 'Utfärdas så snart du bekräftar ditt inbyte',
    },
    rewardNoteAdd: 'Lägg till varor för att se värdet',
    addedManually: 'Tillagd manuellt',
    maxExceeded: 'Detta inbyte överstiger maxvärdet på {amount}. Ta bort en vara för att fortsätta.',
    remove: 'Ta bort {name}',
    back: 'Tillbaka',
    continue: 'Fortsätt',
    confirmTradeIn: 'Bekräfta inbyte',
    done: 'Klart',
  },

  items: {
    firstTitle: 'Låt oss hitta din första vara',
    firstSubtitle: 'Ta ett foto eller sök i katalogen.',
    anotherTitle: 'Lägg till ytterligare en vara?',
    anotherSubtitle: 'Gå igenom allt innan du fortsätter.',
    selectTitle: 'Välj varor att byta in',
    selectSubtitle: 'Från dina tidigare beställningar — tryck för att lägga till.',
    manualTitle: 'Lägg till en vara manuellt',
    manualSubtitle: 'För varor köpta någon annanstans.',
    estReward: 'Värde',
    bought: 'Köpt {date}',
    addManually: 'Lägg till manuellt',
    boughtElsewhere: 'Köpt någon annanstans?',
    addedCount: '{count} tillagda',
    nothingTitle: 'Inget att byta in',
    nothingBody:
      'Vi kunde inte hitta några varor på ditt konto som går att byta in. Inbyte här gäller endast varor köpta hos oss — så snart du har en giltig beställning visas den här.',
  },

  intro: {
    title: 'Så fungerar inbyte',
    lead: 'Byt in {noun} från {brand} som du inte längre bär — och få {rewardPoss} till nytt eller begagnat.',
    step1Title: 'Hitta dina plagg',
    step1Body: 'Ta ett foto på det du inte längre bär, eller sök i vår katalog. Lägg till hur många plagg du vill.',
    step2Title: 'Skicka in dem',
    step2Body: 'Packa allt i ett paket med din förbetalda fraktetikett — frakten bjuder vi på.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'När vi har tagit emot och kontrollerat dina varor mejlar vi {rewardPoss}.',
      handin: 'Så snart ditt paket skannas vid inlämningen mejlar vi {rewardPoss}.',
      instant: '{rewardPoss|cap} mejlas så snart du bekräftar — sedan skickar du dina varor till oss.',
    },
    cta: 'Uppfattat',
    close: 'Stäng',
  },

  finder: {
    search: 'Sök i katalogen',
    photo: 'Lägg till med foto',
    searchTitle: 'Sök i katalogen',
    searchSub: 'På SKU, EAN eller produktnamn.',
    searchPlaceholder: 'Sök på SKU, EAN eller produktnamn',
    carelabelHint: 'Tips: hitta SKU- eller stilnumret på varans skötselråd.',
    searching: 'Söker…',
    searchFailed: 'Sökningen misslyckades — försök igen.',
    noMatches: 'Inga träffar för ”{query}”.',
    unavailable: 'Katalogsökning är inte tillgänglig för den här butiken.',
    estReward: 'Värde',
    notEligible: 'Kan inte bytas in',
    add: 'Lägg till',
    adding: 'Lägger till…',
    added: '{name} tillagd · +{amount}',
    backToOrders: 'Tillbaka till dina beställningar',
    showMore: 'Visa fler resultat',
    loadingMore: 'Hämtar…',
  },

  photo: {
    dropTitle: 'Ta ett foto på din vara',
    dropHint: 'Vi hittar liknande varor i vår katalog.',
    or: 'eller',
    wayDevice: 'Ta eller ladda upp ett foto',
    wayPhone: 'Använd kameran i din telefon',
    qrAgainTitle: 'Prova ett annat foto',
    qrAgainHint: 'Ta ett nytt foto med telefonen, så söker vi automatiskt igen.',
    analysingTitle: 'Hittar dina bästa träffar…',
    analysingHint: 'Jämför ditt foto med vår katalog',
    uploadingTitle: 'Laddar upp ditt foto…',
    uploadingHint: 'Ett ögonblick',
    yourPhoto: 'Ditt foto',
    matchedTo: 'Matchad mot vår katalog',
    replace: 'Ersätt',
    headingConfident: 'Ser någon av dessa ut som din vara?',
    headingCategory: 'Närmaste träffar i {category}',
    noMatchHeading: 'Vi hittade ingen nära träff',
    noMatchBody:
      'Ingen av våra katalogvaror matchade tillräckligt nära. Prova ett annat foto, eller sök på namn i stället.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Inte sökt än',
    searchFailedHeading: 'Vi kunde inte söka just nu',
    searchFailedBody:
      'Något gick fel hos oss, så vi har inte jämfört ditt foto än. Ditt foto är sparat — försök igen.',
    tryAgain: 'Försök igen',
    refine: 'Filtrera efter kategori',
    clear: 'Rensa',
    noneOfThese: 'Ingen av dessa',
    // "Lägg till efter kategori" — reserven från platshållarrutan i fotoresultaten.
    categoryCardTitle: 'Hittar du inte din vara?',
    categoryCardHint: 'Välj en kategori i stället',
    categoryPickTitle: 'Vilken typ av vara är det?',
    categoryPickHint: 'Vi använder fotot du precis tog.',
    categoryValue: 'Värde',
    categoryConfirm: 'Lägg till i inbytet',
    categoryBack: 'Tillbaka',
    uploadAnother: 'Ladda upp ett annat foto',
    bestMatch: 'Bästa träff',
    uploadFailed: 'Något gick fel — försök igen.',
  },

  match: {
    strong: 'Stark träff',
    colour: 'Färg: {color}',
    estimatedReward: 'Värde',
    backToMatches: 'Tillbaka till träffarna',
    backToResults: 'Tillbaka till resultaten',
    confirm: 'Ja, det är min vara',
  },

  details: {
    title: 'Dina uppgifter',
    subtitle: 'Så att vi kan skapa din förbetalda fraktetikett och skicka {rewardPoss}.',
    contact: 'Kontakt',
    address: 'Adress',
    fullName: 'Fullständigt namn',
    email: 'E-post',
    country: 'Land',
    streetAddress: 'Gatuadress',
    postalCode: 'Postnummer',
    city: 'Stad',
    prefix: 'Landskod',
    phone: 'Telefon',
    emailLocked: 'Din e-postadress är kopplad till ditt konto och kan inte ändras här.',
    consentPre: 'Jag accepterar ',
    consentLink: 'villkoren för inbyte',
    consentPost: ' och bekräftar att varorna är mina att byta in.',
    required: 'Detta fält är obligatoriskt.',
    emailInvalid: 'Ange en giltig e-postadress.',
    phoneInvalid: 'Ange ett giltigt telefonnummer.',
    zipInvalid: 'Ange ett giltigt postnummer',
    zipFormat: 'format: {format}',
    loading: 'Hämtar dina uppgifter…',
    saveFailed: 'Vi kunde inte spara dina uppgifter. Försök igen.',
  },

  terms: {
    title: 'Villkor för inbyte av {noun}',
    // {brand} är butikens varumärkesnamn (config.brandName), eller en generisk reserv om det saknas.
    introLead:
      'Som en del av vårt secondhand-initiativ kan du byta in {nounPoss} från {brand} mot ett {reward}.',
    introAccept:
      'Genom att skicka in {nounPoss} från {brand} accepterar du att {reward} endast utfärdas för {noun} som uppfyller villkoren nedan, och att {noun} som inte uppfyller kraven INTE skickas tillbaka till dig.',
    introDonate:
      '{noun|cap} som inte uppfyller våra krav doneras till välgörenhet eller ges till företag som kan återanvända {nounDef} eller materialet i dem.',
    requirementsTitle: 'Krav på {nounDef} du lämnar in',
    requirementsBody:
      'Vi tar emot {noun} från {brand} som är rena och där alla originaletiketter är intakta — både nacketiketten och skötselrådet (tvättanvisningar m.m.).',
    howToTitle: 'Så lämnar du in {nounPoss}',
    howToRegister:
      'Du registrerar de {brand}-produkter du vill lämna in. Under registreringen ser du värdet på varje produkt — beloppet du får som {reward} i utbyte mot {nounPoss}.',
    howToAdjust:
      'Om det vi tar emot inte stämmer med det du registrerat rättar vi till rätt produkt och justerar ditt totala värde därefter. Genom att skicka in {nounPoss} accepterar du att vi gör denna justering.',
    howToLabel:
      'När du har slutfört din registrering får du en fraktetikett eller QR-kod för att skicka in {nounPoss}. Fraktetiketten är förbetald av {brand}.',
    howToFinal:
      'När du har lämnat in {nounPoss} kan du inte ångra din inlämning, och vi skickar inte tillbaka dina produkter.',
    voucherTitle: '{reward|cap}',
    voucherIssue: {
      received:
        'Du får ett {reward} när vi har bekräftat att {nounPoss} uppfyller kraven ovan. {rewardDef|cap} skickas till den e-postadress du angett.',
      handin:
        'Du får ett {reward} när ditt paket har registrerats på inlämningsstället. {rewardDef|cap} skickas till den e-postadress du angett. Om {nounPoss} inte uppfyller kraven ovan kan {rewardDef} dras tillbaka.',
      instant:
        'Du får ett {reward} så snart du registrerar ditt inbyte. {rewardDef|cap} skickas till den e-postadress du angett. Om {nounPoss} inte skickas till oss, eller inte uppfyller kraven ovan, kan {rewardDef} dras tillbaka.',
    },
    voucherValidity:
      '{rewardDef|cap} gäller i 3 år från utfärdandet och kan användas för köp hos {brand} online. {rewardDef|cap} kan inte lösas in mot kontanter.',
    closing: 'Vi ser fram emot att ta emot {nounPoss}.',
    brandFallback: 'varumärket',
    close: 'Stäng',
    accept: 'Acceptera & fortsätt',
  },

  shipping: {
    title: 'Hur vill du skicka in?',
    subtitle: 'Packa din {count} vara — en etikett för allt.',
    subtitlePlural: 'Packa dina {count} varor — en etikett för allt.',
    loading: 'Hämtar fraktalternativ…',
    none: 'Det finns ännu ingen frakttjänst för din adress.',
    maxExceeded: 'Detta inbyte överstiger maxvärdet. Ta bort en vara och försök igen.',
    createFailed: 'Vi kunde inte skapa ditt inbyte. Försök igen.',
    desc_gls: 'Lämna in hos ett GLS-paketombud. Gratis, utskriven etikett.',
    desc_dhl: 'Lämna in hos ett DHL-paketombud. Gratis, utskriven etikett.',
    desc_dao: 'En kurir hämtar vid din dörr.',
    desc_shipmondo: 'Lämna in med en utskriven etikett.',
    desc_pickup: 'Lämna in i vår fysiska butik.',
    desc_postnord: 'Lämna in hos valfritt PostNord-ombud.',
  },

  story: {
    title: 'Berätta din historia',
    subtitle: 'Valfritt — vi älskar att höra om dina plagg.',
    placeholder: 'Berätta om det här plagget — var du bar det, varför du älskade det…',
    consent:
      'Ni får dela min berättelse (anonymt) för att inspirera andra kunder att ge sina {noun} ett nytt liv.',
  },

  confirm: {
    topbarTitle: 'Bekräfta',
    heroTitle: 'Då är du klar',
    heroBody: {
      received: 'Ditt inbyte är registrerat. Följ de tre stegen nedan, så är {rewardPoss} på väg.',
      handin:
        'Ditt inbyte är registrerat. Följ de tre stegen nedan — {rewardPoss} utfärdas så snart du lämnar in ditt paket.',
      instant:
        'Ditt inbyte är registrerat och {rewardPoss} är på väg. Följ bara de tre stegen nedan för att skicka dina varor till oss.',
    },
    packTitle: 'Packa din {count} vara',
    packTitlePlural: 'Packa dina {count} varor',
    packBody: 'Lägg allt i ett paket. Ingen särskild förpackning behövs — återanvänd en kartong eller påse.',
    pickupPackBody: 'Packa dina varor och ta med dem till vår fysiska butik — ingen fraktetikett behövs.',
    shippingCode: 'Fraktkod',
    generatingLabel: 'Skapar din fraktetikett…',
    labelFailed: 'Vi kunde inte skapa din etikett. Kontakta kundtjänst.',
    showQr: 'Visa QR-kod',
    showLabel: 'Visa fraktetikett',
    dropTitle: 'Lämna in det',
    dropBody: 'Visa koden eller etiketten hos fraktbolagets inlämningsställe. ',
    findShop: 'Hitta närmaste inlämningsställe →',
    pickupTitle: 'Lämna in i butik',
    pickupBody: 'Ta med dina varor till vår butik, så sköter vi resten.',
    rewardTitle: 'Få {rewardPoss}',
    // Delad runt beloppet, som ConfirmPage renderar i <b> mellan halvorna ("…mejlar vi ditt [500 kr]
    // {reward}"). Precis som på danska sitter bestämningen "ditt" i den här halvan och följer alltså
    // INTE varumärkets ord: en butik med ett utrum-ord ("din kupong") måste även åsidosätta dessa tre
    // rader. Neutrumord (värdebevis, presentkort) påverkas inte.
    rewardBodyPre: {
      received: 'När vi tar emot ditt paket mejlar vi ditt ',
      handin: 'Så snart ditt paket skannas vid inlämningen mejlar vi ditt ',
      instant: 'Ditt ',
    },
    rewardBodyPost: {
      received: ' {reward} inom 2 arbetsdagar.',
      handin: ' {reward}. Ge det gärna ett par timmar innan inlämningens spårningsdata når oss.',
      instant: ' {reward} är redan på väg till din inkorg — skicka bara dina varor till oss enligt stegen ovan.',
    },
    // INSTANT-strategi: värdebeviskoden visas här så snart Core har utfärdat den.
    voucherReadyTitle: '{rewardPoss|cap} är klart',
    voucherIssuing: 'Skapar {rewardPoss}…',
    voucherExpires: 'går ut {date}',
    copyCode: 'Kopiera {reward}kod',
    codeCopied: '{reward|cap}kod kopierad',
    startAnother: 'Starta ett nytt inbyte',
    backToShop: 'Tillbaka till butiken',
  },

  dropoff: {
    title: 'Närmaste inlämningsställen',
    qrSupported: 'Stöder QR-inlämning — ingen utskriven etikett behövs.',
    qrNotSupported: 'Kräver en utskriven etikett.',
  },
}
